import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Star, Pencil, Heart, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import ShareButton from "@/components/ShareButton";
import WhatsAppShareButton from "@/components/WhatsAppShareButton";
import RecipeParamsDisplay from "@/components/RecipeParamsDisplay";
import RecipeFeedback from "@/components/RecipeFeedback";
import SimilarRecipes from "@/components/SimilarRecipes";
import { resolveAssetUrl } from "@/components/ImageUploader";
import { Link as RLink } from "react-router-dom";

const EXPECTED_EXTRACTION = {
  espresso: { ey: [18, 22], tds: [8, 12], unit_tds: "%" },
  moka: { ey: [18, 22], tds: [8, 12], unit_tds: "%" },
  v60: { ey: [18, 22], tds: [1.15, 1.45], unit_tds: "%" },
  chemex: { ey: [18, 22], tds: [1.15, 1.45], unit_tds: "%" },
  aeropress: { ey: [18, 22], tds: [1.2, 1.5], unit_tds: "%" },
  frenchpress: { ey: [18, 22], tds: [1.25, 1.55], unit_tds: "%" },
  coldbrew: { ey: [18, 22], tds: [1.0, 1.4], unit_tds: "%" },
  turkish: { ey: [22, 26], tds: [2.0, 3.5], unit_tds: "%" },
};

const ExpectedExtractionBadge = ({ method }) => {
  const { isRTL } = useI18n();
  const m = (method || "").toLowerCase();
  const range = EXPECTED_EXTRACTION[m];
  if (!range) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest" data-testid="expected-extraction-badge">
      <span className="px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
        {isRTL ? "هدف الاستخلاص" : "Target EY"}: {range.ey[0]}–{range.ey[1]}%
      </span>
      <span className="px-2 py-1 rounded-full bg-[#F9F6F0] border border-[#2A2421]/15 text-[#4A5D4E]">
        TDS: {range.tds[0]}–{range.tds[1]}{range.unit_tds}
      </span>
      <RLink to="/calculator?tab=extraction" className="text-[#C86240] hover:underline text-[10px] normal-case tracking-normal" data-testid="open-extraction-calc-link">
        {isRTL ? "افتح حاسبة الاستخلاص ←" : "Open extraction calculator →"}
      </RLink>
    </div>
  );
};

// Method-based image fallback so new recipes always look right
const METHOD_IMG = {
  v60: "https://images.unsplash.com/photo-1545665225-b23b99e4d45e?auto=format&fit=crop&w=1200&q=80",
  chemex: "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&w=1200&q=80",
  espresso: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=1200&q=80",
  frenchpress: "https://images.unsplash.com/photo-1485808191679-5f86510681a2?auto=format&fit=crop&w=1200&q=80",
  aeropress: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80",
  moka: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=1200&q=80",
  coldbrew: "https://images.unsplash.com/photo-1517701604599-bb29b565090c?auto=format&fit=crop&w=1200&q=80",
  turkish: "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?auto=format&fit=crop&w=1200&q=80",
};
const recipeImg = (r) => resolveAssetUrl(r?.image_url) || METHOD_IMG[(r?.method || "").toLowerCase()] || null;

export default function Recipes() {
  const { t, lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const { user } = useAuth();
  const isAdmin = useAdmin();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["recipes"], queryFn: endpoints.recipes });
  const { data: favData } = useQuery({
    queryKey: ["favoriteIds"],
    queryFn: endpoints.favoriteIds,
    enabled: !!user,
  });
  const favoriteIds = new Set(favData?.ids || []);
  const [openState, setOpenState] = useState(null);

  // Deep-link support: /recipes?recipe=<id> opens the recipe dialog automatically.
  // We derive the effective open recipe at render time to avoid setState-in-effect.
  const [searchParams] = useSearchParams();
  const deepRecipeId = searchParams.get("recipe");
  const [deepLinkDismissed, setDeepLinkDismissed] = useState(false);
  const deepLinkRecipe = React.useMemo(() => {
    if (!deepRecipeId || !data || deepLinkDismissed) return null;
    const all = [...(data.curated || []), ...(data.community || [])];
    return all.find((r) => r.id === deepRecipeId) || null;
  }, [deepRecipeId, data, deepLinkDismissed]);
  const open = openState || deepLinkRecipe;
  const setOpen = (r) => {
    setOpenState(r);
    if (r === null && deepLinkRecipe) setDeepLinkDismissed(true);
  };

  const curated = data?.curated || [];
  const community = data?.community || [];
  const [sortMode, setSortMode] = useState("newest");
  const sortRecipes = React.useCallback((arr) => {
    const a = [...arr];
    if (sortMode === "top") return a.sort((x, y) => (y.avg_rating || 0) - (x.avg_rating || 0) || (y.ratings_count || 0) - (x.ratings_count || 0));
    if (sortMode === "popular") return a.sort((x, y) => (y.ratings_count || 0) - (x.ratings_count || 0));
    return a; // newest (default order from backend)
  }, [sortMode]);
  const curatedSorted = sortRecipes(curated);
  const communitySorted = sortRecipes(community);

  const onDelete = async (id) => {
    try {
      await endpoints.deleteRecipe(id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["recipes"] });
      setOpen(null);
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Could not delete");
    }
  };

  const onPromote = async (id) => {
    try {
      await endpoints.promoteRecipe(id);
      const isCommunity = open?.is_community;
      toast.success(
        isCommunity
          ? (isRTL ? "تم النقل إلى المنتقاة" : "Moved to Curated")
          : (isRTL ? "تم النقل إلى المجتمع" : "Moved to Community")
      );
      qc.invalidateQueries({ queryKey: ["recipes"] });
      setOpen(null);
    } catch {
      toast.error(isRTL ? "تعذّر النقل" : "Could not move");
    }
  };

  const onToggleFav = async (e, id) => {
    e.stopPropagation();
    try {
      const res = await endpoints.toggleFavorite(id);
      toast.success(
        res.favorited
          ? (isRTL ? "أُضيفت للمفضلة" : "Added to favorites")
          : (isRTL ? "أُزيلت من المفضلة" : "Removed from favorites")
      );
      qc.invalidateQueries({ queryKey: ["favoriteIds"] });
      qc.invalidateQueries({ queryKey: ["favoriteRecipes"] });
    } catch {
      toast.error(isRTL ? "حدث خطأ" : "Something went wrong");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="recipes-page">
      <Seo
        title={
          open
            ? (lang === "ar" ? (open.title_ar || open.title_en) : (open.title_en || open.title_ar))
            : (isRTL ? "مكتبة الوصفات" : "Recipe Library")
        }
        description={
          open
            ? (lang === "ar"
                ? `وصفة ${open.title_ar || open.title_en} · طريقة ${open.method} · من مكتبة عبدالله | علم القهوة`
                : `${open.title_en || open.title_ar} recipe · ${open.method} · Abdullah Coffee Science Academy`)
            : (isRTL
                ? "مكتبة وصفات قهوة مختصة بالعربي — كلاسيكيات، إسبريسو، فلتر، وأكثر من باريستا المنطقة."
                : "Specialty coffee recipe library — classics, espresso, filter and more from regional baristas.")
        }
        path={open ? `/recipes?recipe=${open.id}` : "/recipes"}
        image={open ? (recipeImg(open) || undefined) : undefined}
        type={open ? "article" : "website"}
        jsonLdId={open ? `recipe-${open.id}-jsonld` : "recipes-jsonld"}
      />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-4">{isRTL ? "مكتبة" : "Library"}</div>
          <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>{t.recipes.title}</h1>
          <p className="mt-6 text-[#5C544F] leading-relaxed">{t.recipes.lead}</p>
        </div>
        <Link to="/recipes/new" data-testid="add-recipe-btn">
          <Button className="rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0] px-6 py-5">
            <Plus className="w-4 h-4 me-2" /> {t.recipes.add}
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="curated" className="w-full">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <TabsList className="bg-[#F9F6F0] border border-[#2A2421]/10 rounded-full p-1 h-auto">
            <TabsTrigger value="curated" className="rounded-full px-5 py-2 data-[state=active]:bg-[#2A2421] data-[state=active]:text-[#F9F6F0]" data-testid="tab-curated">
              {t.recipes.curated}
            </TabsTrigger>
            <TabsTrigger value="community" className="rounded-full px-5 py-2 data-[state=active]:bg-[#2A2421] data-[state=active]:text-[#F9F6F0]" data-testid="tab-community">
              {t.recipes.community} <span className="ms-1.5 text-xs opacity-70">({community.length})</span>
            </TabsTrigger>
          </TabsList>
          <div className="inline-flex rounded-full border border-[#2A2421]/15 p-0.5 bg-white text-xs">
            <button
              onClick={() => setSortMode("newest")}
              className={`px-3 py-1.5 rounded-full ${sortMode === "newest" ? "bg-[#2A2421] text-[#F9F6F0]" : "text-[#5C544F]"}`}
              data-testid="recipes-sort-newest"
            >
              {isRTL ? "الأحدث" : "Newest"}
            </button>
            <button
              onClick={() => setSortMode("top")}
              className={`px-3 py-1.5 rounded-full flex items-center gap-1 ${sortMode === "top" ? "bg-[#C86240] text-white" : "text-[#5C544F]"}`}
              data-testid="recipes-sort-top"
            >
              <Star className="w-3 h-3" />
              {isRTL ? "الأعلى تقييماً" : "Top rated"}
            </button>
            <button
              onClick={() => setSortMode("popular")}
              className={`px-3 py-1.5 rounded-full ${sortMode === "popular" ? "bg-[#2A2421] text-[#F9F6F0]" : "text-[#5C544F]"}`}
              data-testid="recipes-sort-popular"
            >
              {isRTL ? "الأكثر تقييماً" : "Most rated"}
            </button>
          </div>
        </div>

        <TabsContent value="curated" className="mt-6">
          {isLoading ? (
            <div className="text-[#5C544F]">{t.common.loading}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {curatedSorted.map((r) => (
                <RecipeCard
                  key={r.id}
                  r={r}
                  onOpen={() => setOpen(r)}
                  isFavorite={favoriteIds.has(r.id)}
                  canFavorite={!!user}
                  onToggleFav={onToggleFav}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="community" className="mt-6">
          {community.length === 0 ? (
            <div className="soft-card rounded-2xl p-10 text-center" data-testid="community-empty">
              <p className="text-[#5C544F] mb-4">{t.recipes.empty}</p>
              <Link to="/recipes/new">
                <Button className="rounded-full bg-[#C86240] hover:bg-[#b15637] text-white">
                  <Plus className="w-4 h-4 me-2" /> {t.recipes.add}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {communitySorted.map((r) => (
                <RecipeCard
                  key={r.id}
                  r={r}
                  onOpen={() => setOpen(r)}
                  isFavorite={favoriteIds.has(r.id)}
                  canFavorite={!!user}
                  onToggleFav={onToggleFav}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#F9F6F0] p-0" data-testid="recipe-dialog">
          {open && (
            <>
              {recipeImg(open) && (
                <div className="w-full aspect-[16/9] overflow-hidden bg-[#2A2421]">
                  <img src={recipeImg(open)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <DialogHeader className="px-5 pt-5 pb-0 bg-[#F9F6F0]">
                <DialogTitle className={`${serifClass} text-3xl text-[#2A2421] text-start`}>
                  {lang === "ar" ? open.title_ar : open.title_en}
                </DialogTitle>
              </DialogHeader>
              <div className="p-5 space-y-5 bg-[#F9F6F0]">
                <div className="flex justify-end gap-2 flex-wrap">
                  <WhatsAppShareButton
                    text={
                      lang === "ar"
                        ? `وصفة ${open.title_ar} على عبدالله | علم القهوة`
                        : `${open.title_en} recipe on Abdullah | Coffee Science`
                    }
                    url={`https://abdullah-coffee-science.academy/recipes?recipe=${open.id}`}
                    label={lang === "ar" ? "واتساب" : "WhatsApp"}
                    testId={`whatsapp-share-recipe-${open.id}`}
                  />
                  <ShareButton
                    title={(lang === "ar" ? open.title_ar : open.title_en) + " · عبدالله | علم القهوة"}
                    text={
                      lang === "ar"
                        ? `وصفة ${open.title_ar} على عبدالله | علم القهوة`
                        : `${open.title_en} recipe on Abdullah | Coffee Science`
                    }
                    url={`https://abdullah-coffee-science.academy/recipes?recipe=${open.id}`}
                    label={lang === "ar" ? "مشاركة الوصفة" : "Share recipe"}
                    variant="outline"
                    testId={`share-recipe-${open.id}`}
                  />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F]">
                  {t.recipes.by} {open.user_name || "Brew Library"} · {t.recipes.method}: {open.method}
                </div>
                {open.params && (
                  <div className="bg-white rounded-2xl p-4 border border-[#2A2421]/10">
                    <RecipeParamsDisplay params={open.params} />
                  </div>
                )}
                <ExpectedExtractionBadge method={open.method} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl p-4 border border-[#2A2421]/10">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{t.recipes.ingredients}</div>
                    <ul className="space-y-1.5 text-[#2A2421] text-sm leading-relaxed">
                      {(lang === "ar" ? open.ingredients_ar : open.ingredients_en).map((it) => (
                        <li key={it}>· {it}</li>
                      ))}
                    </ul>
                  </div>
                  {((lang === "ar" ? open.steps_ar : open.steps_en) || []).length > 0 && (
                    <div className="bg-white rounded-2xl p-4 border border-[#2A2421]/10">
                      <div className="text-xs uppercase tracking-[0.25em] text-[#4A5D4E] mb-3">{t.recipes.steps}</div>
                      <ol className="space-y-2 text-[#2A2421] text-sm leading-relaxed">
                        {(lang === "ar" ? open.steps_ar : open.steps_en).map((it, i) => (
                          <li key={it}>
                            <span className="text-[#C86240] me-2 font-semibold">{i + 1}.</span> {it}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
                <RecipeFeedback recipeId={open.id} />
                <SimilarRecipes recipeId={open.id} onPick={(r) => setOpen(r)} />
              {user && (isAdmin || open.user_id === user.user_id) && (
                <div className="pt-4 border-t border-[#2A2421]/10 flex flex-wrap justify-end gap-2">
                  {isAdmin && (
                    <Button
                      onClick={() => onPromote(open.id)}
                      variant="outline"
                      className="rounded-full border-[#C86240]/40 text-[#C86240] hover:bg-[#C86240]/10"
                      data-testid="promote-recipe-btn"
                    >
                      {open.is_community ? (
                        <>
                          <ArrowUpCircle className="w-4 h-4 me-2" />
                          {isRTL ? "نقل إلى المنتقاة" : "Move to Curated"}
                        </>
                      ) : (
                        <>
                          <ArrowDownCircle className="w-4 h-4 me-2" />
                          {isRTL ? "نقل إلى المجتمع" : "Move to Community"}
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate(`/recipes/${open.id}/edit`)}
                    variant="outline"
                    className="rounded-full"
                    data-testid="edit-recipe-btn"
                  >
                    <Pencil className="w-4 h-4 me-2" /> {isRTL ? "تعديل" : "Edit"}
                  </Button>
                  <Button
                    onClick={() => {
                      if (window.confirm(isRTL ? "تأكيد الحذف؟" : "Confirm delete?")) onDelete(open.id);
                    }}
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50"
                    data-testid="delete-recipe-btn"
                  >
                    <Trash2 className="w-4 h-4 me-2" /> {t.recipes.delete}
                  </Button>
                </div>
              )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const RecipeCard = ({ r, onOpen, isFavorite = false, canFavorite = false, onToggleFav }) => {
  const { lang, t, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  return (
    <button
      onClick={onOpen}
      data-testid={`recipe-card-${r.id}`}
      className="text-start group soft-card rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform relative"
    >
      {r.avg_rating >= 4.5 && r.ratings_count >= 1 && (
        <span
          className="absolute top-3 start-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-gradient-to-r from-[#C86240] to-[#a04d31] text-white shadow-lg"
          data-testid={`top-rated-${r.id}`}
        >
          <Star className="w-3 h-3 fill-white" />
          {isRTL ? "الأعلى تقييماً" : "Top rated"}
        </span>
      )}
      {canFavorite && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => onToggleFav?.(e, r.id)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onToggleFav?.(e, r.id); }}
          data-testid={`fav-toggle-${r.id}`}
          aria-label={isFavorite ? (isRTL ? "إزالة من المفضلة" : "Remove favorite") : (isRTL ? "أضف للمفضلة" : "Add to favorites")}
          aria-pressed={isFavorite}
          className="absolute top-3 end-3 z-10 w-10 h-10 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center transition-transform hover:scale-110 cursor-pointer"
        >
          <Heart
            className={`w-5 h-5 ${isFavorite ? "fill-[#C86240] text-[#C86240]" : "text-[#2A2421]"}`}
          />
        </span>
      )}
      <div className="aspect-[4/3] bg-[#F9F6F0] overflow-hidden">
        {recipeImg(r) ? (
          <img src={recipeImg(r)} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#C86240]">
            <Star className="w-8 h-8 opacity-30" />
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">
          {r.method} · {t.difficulty[r.difficulty] || r.difficulty}
        </div>
        <div className={`${serifClass} text-2xl text-[#2A2421] leading-tight`}>
          {lang === "ar" ? r.title_ar : r.title_en}
        </div>
        <div className="mt-3 text-xs text-[#5C544F] flex items-center justify-between gap-2">
          <span>{t.recipes.by} <span className="text-[#2A2421]">{r.user_name || "Brew Library"}</span></span>
          {r.ratings_count > 0 && (
            <span className="inline-flex items-center gap-1 text-[#C86240]">
              <Star className="w-3 h-3 fill-[#C86240]" />
              <span className="font-semibold">{(r.avg_rating || 0).toFixed(1)}</span>
              <span className="text-[#5C544F]">({r.ratings_count})</span>
            </span>
          )}
        </div>
      </div>
    </button>
  );
};
